import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  useColorModeValue,
  useToast,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  InputGroup,
  InputRightElement,
  IconButton,
  FormHelperText
} from '@chakra-ui/react'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authService } from '../config/index'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const [token, setToken] = useState('')
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const [isValidToken, setIsValidToken] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  
  // Mover todos los hooks de color al inicio para evitar errores de React Hooks
  const bgColor = useColorModeValue('whiteAlpha.900', 'blackAlpha.600')
  const inputBgColor = useColorModeValue('white', 'whiteAlpha.50')
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100')

  useEffect(() => {
    const validateToken = async () => {
      const tokenParam = searchParams.get('token')
      
      if (!tokenParam) {
        toast({
          title: 'Token inválido',
          description: 'No se proporcionó un token de recuperación válido',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        navigate('/login')
        return
      }

      setToken(tokenParam)
      setIsValidating(true)

      try {
        const userEmail = await authService.validatePasswordResetToken(tokenParam)
        
        if (!userEmail) {
          toast({
            title: 'Token expirado o inválido',
            description: 'El enlace de recuperación ha expirado o no es válido. Por favor, solicita uno nuevo.',
            status: 'error',
            duration: 8000,
            isClosable: true,
          })
          navigate('/login')
          return
        }

        setEmail(userEmail)
        setIsValidToken(true)
      } catch (error) {
        console.error('Error al validar token:', error)
        toast({
          title: 'Error de validación',
          description: 'No se pudo validar el token de recuperación',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        navigate('/login')
      } finally {
        setIsValidating(false)
      }
    }

    validateToken()
  }, [searchParams, navigate, toast])

  const validatePassword = (password: string): string[] => {
    const errors: string[] = []
    
    if (password.length < 8) {
      errors.push('Debe tener al menos 8 caracteres')
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Debe contener al menos una letra mayúscula')
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Debe contener al menos una letra minúscula')
    }
    
    if (!/\d/.test(password)) {
      errors.push('Debe contener al menos un número')
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Debe contener al menos un carácter especial')
    }
    
    return errors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newPassword || !confirmPassword) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor, completa todos los campos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Las contraseñas no coinciden',
        description: 'Por favor, verifica que ambas contraseñas sean iguales',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const passwordErrors = validatePassword(newPassword)
    if (passwordErrors.length > 0) {
      toast({
        title: 'Contraseña no válida',
        description: passwordErrors.join(', '),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)

    try {
      const success = await authService.resetPasswordWithToken(token, newPassword)
      
      if (!success) {
        toast({
          title: 'Error al restablecer contraseña',
          description: 'El token ha expirado o no es válido',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      toast({
        title: 'Contraseña restablecida',
        description: 'Tu contraseña ha sido restablecida exitosamente. Ahora puedes iniciar sesión con tu nueva contraseña.',
        status: 'success',
        duration: 8000,
        isClosable: true,
      })
      
      // Redirigir al login después de un breve delay
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (error) {
      console.error('Error al restablecer contraseña:', error)
      toast({
        title: 'Error',
        description: 'No se pudo restablecer la contraseña. Por favor, inténtalo de nuevo.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isValidating) {
    return (
      <Box 
        minH="100vh" 
        backgroundImage="url('/silla.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.700"
        />
        <Container maxW="container.sm" py={{ base: 8, md: 20 }} position="relative" zIndex={1}>
          <VStack spacing={8} align="stretch">
            <Heading
              textAlign="center"
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              bgGradient="linear(to-r, brand.600, brand.500, brand.400)"
              bgClip="text"
            >
              Validando enlace...
            </Heading>
            <Box textAlign="center">
              <Text>Por favor, espera mientras validamos tu enlace de recuperación.</Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    )
  }

  if (!isValidToken) {
    return (
      <Box 
        minH="100vh" 
        backgroundImage="url('/silla.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.700"
        />
        <Container maxW="container.sm" py={{ base: 8, md: 20 }} position="relative" zIndex={1}>
          <VStack spacing={8} align="stretch">
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Enlace inválido o expirado</AlertTitle>
                <AlertDescription>
                  El enlace de recuperación no es válido o ha expirado. Por favor, solicita uno nuevo.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </Container>
      </Box>
    )
  }

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.sm" py={{ base: 8, md: 20 }} position="relative" zIndex={1}>
        <VStack spacing={8} align="stretch">
          <Heading
            textAlign="center"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="bold"
            bgGradient="linear(to-r, brand.600, brand.500, brand.400)"
            bgClip="text"
            letterSpacing="tight"
          >
            Restablecer Contraseña
          </Heading>

          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Restableciendo contraseña para:</AlertTitle>
              <AlertDescription>{email}</AlertDescription>
            </Box>
          </Alert>

          <Box
            as="form"
            onSubmit={handleSubmit}
            bg={bgColor}
            p={{ base: 6, md: 8 }}
            borderRadius="2xl"
            boxShadow="2xl"
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={borderColor}
          >
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Nueva Contraseña</FormLabel>
                <InputGroup size="lg">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    bg={inputBgColor}
                    borderWidth="2px"
                    _hover={{
                      borderColor: 'brand.400',
                    }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: 'outline',
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      size="sm"
                      color="gray.500"
                      _hover={{
                        color: 'brand.500',
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormHelperText>
                  Debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas, números y caracteres especiales
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="medium">Confirmar Nueva Contraseña</FormLabel>
                <InputGroup size="lg">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    bg={inputBgColor}
                    borderWidth="2px"
                    _hover={{
                      borderColor: 'brand.400',
                    }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: 'outline',
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                      variant="ghost"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      size="sm"
                      color="gray.500"
                      _hover={{
                        color: 'brand.500',
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                width="full"
                isLoading={isLoading}
                loadingText="Restableciendo..."
                fontSize="md"
                fontWeight="bold"
                py={7}
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                }}
              >
                Restablecer Contraseña
              </Button>
              
              <Text textAlign="center" mt={2} fontSize="sm" color="gray.600">
                ¿Recordaste tu contraseña?{' '}
                <Button
                  variant="link"
                  color="brand.500"
                  fontWeight="medium"
                  onClick={() => navigate('/login')}
                  size="sm"
                >
                  Volver al inicio de sesión
                </Button>
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
